import { useMemo } from "react";

import { Button } from "@components/Button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@components/DropdownMenu";
import { Image } from "@components/Image";
import { Stack } from "@components/Stack";
import { Typography } from "@components/Typography";

import { useHover } from "@hooks/useHover";
import { useLayoutBreakpoint } from "@hooks/useLayoutBreakpoint";

import { EBroadcastStatus } from "@api/broadcast";

import { DIALOG_KEYS, useDialogStore } from "@storage/global/useDialogStore";
import { useBroadcastStatusDataQuery } from "@storage/queries/broadcast/useBroadcastStatusDataQuery";
import { deleteGoalByIdMutation, getGoalListQuery, startGoalMutation } from "@storage/queries/goals";

import { cn } from "@lib/utils";

import { SKELETON_BLOCK_CLASS } from "@configs/constants";
import { ICON_PATHS } from "@configs/icons";

export const GoalsContainer = () => {
	const { COMPACT_GOALS } = useLayoutBreakpoint();
	const { broadcastStatus } = useBroadcastStatusDataQuery();
	const {
		data: goalListData,
		isSuccess: isSuccessGoalList,
		isPending: isPendingGoalList,
		isFetching: isFetchingGoalList,
	} = getGoalListQuery();

	const { mutate: startGoal, isPending: isStartGoalPending } = startGoalMutation();
	const { mutate: deleteGoal, isPending: isDeleteGoalPending } = deleteGoalByIdMutation();
	const { openDialog } = useDialogStore();
	const [compactGoalContainerRef, isCompactGoalContainerHovered] = useHover<HTMLDivElement>();

	const isGoalActionPending = isStartGoalPending || isDeleteGoalPending || isFetchingGoalList;
	const isGoalDisabled = broadcastStatus === EBroadcastStatus.PRIVATE || broadcastStatus === EBroadcastStatus.OFFLINE;

	const firstGoalInQueue = useMemo(() => {
		if (!isSuccessGoalList) return null;

		if (goalListData.activeGoal !== null) return goalListData.activeGoal;

		if (goalListData.goalQueue.length > 0) return goalListData.goalQueue[0];

		return null;
	}, [goalListData, isSuccessGoalList]);

	const handleOpenDeleteGoalDialog = () => {
		if (!isSuccessGoalList) return;

		if (goalListData.activeGoal !== null) {
			openDialog(DIALOG_KEYS.DELETE_GOAL, {
				goalId: goalListData.activeGoal.id || "",
				title: goalListData.activeGoal.title,
			});
			return;
		}

		if (firstGoalInQueue) {
			deleteGoal(firstGoalInQueue.id || "");
		}
	};

	const handleOpenGoalSettingsDialog = () => {
		openDialog(DIALOG_KEYS.GOAL_SETTINGS);
	};

	const handleStartGoal = () => {
		startGoal();
	};

	const getPercent = () => {
		if (!isSuccessGoalList) return 0;

		if (firstGoalInQueue === null || firstGoalInQueue.target_tokens <= 0) {
			return 0;
		}

		const currentTokens = Math.max(firstGoalInQueue.current_tokens ?? 0, 0);
		return Math.min((currentTokens / firstGoalInQueue.target_tokens) * 100, 100);
	};

	const goalPercent = getPercent();
	const isGoalPending = firstGoalInQueue?.status === "pending";
	const isGoalInProgress = firstGoalInQueue?.status === "in_progress";
	const isGoalMuted = isGoalPending || isGoalDisabled;

	return COMPACT_GOALS ? (
		<div className="mx-1 mb-1.5" ref={compactGoalContainerRef}>
			{isPendingGoalList ? (
				<div
					className={cn(
						"flex h-7.5 w-full items-center gap-2.5 rounded-2xl border border-primary bg-block px-2.5 py-0.75",
					)}
				>
					<div className={cn(SKELETON_BLOCK_CLASS, "h-3 flex-1")} />
					<div className={cn(SKELETON_BLOCK_CLASS, "size-6 rounded-full")} />
				</div>
			) : isSuccessGoalList ? (
				<div
					className={cn(
						"flex h-7.5 w-full items-center gap-2.5 rounded-2xl border border-primary bg-block px-2.5 py-0.75",
					)}
				>
					{firstGoalInQueue !== null ? (
						<div className="flex min-w-0 flex-1 items-center justify-center gap-1.25">
							<Typography type="span" size="xs" color={isGoalPending ? "grey-tertiary" : "text"}>
								{firstGoalInQueue.current_tokens ?? 0}/{firstGoalInQueue.target_tokens} tk
							</Typography>
							{isCompactGoalContainerHovered ? (
								<div
									className={`flex min-w-0 flex-1 items-center justify-center gap-0.75 overflow-hidden
										whitespace-nowrap`}
									title={firstGoalInQueue.title}
								>
									<Typography
										weight="bold"
										type="span"
										size="xs"
										className={cn("text-[#EED16B]", isGoalMuted && "text-grey-tertiary")}
									>
										Goal:
									</Typography>
									<Typography
										weight="bold"
										type="span"
										size="xs"
										className={cn(
											"block overflow-hidden text-ellipsis whitespace-nowrap",
											isGoalMuted && "text-grey-tertiary",
										)}
									>
										{firstGoalInQueue.title}
									</Typography>
								</div>
							) : (
								<div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-[5px] bg-[#36333c]">
									<div
										className={cn(
											"h-full rounded-[inherit] transition-[width] duration-200 ease-in-out",
											isGoalDisabled ? "bg-grey-tertiary" : "bg-[linear-gradient(90deg,#2787fa_0%,#f026ab_100%)]",
										)}
										style={{ width: `${goalPercent}%` }}
									/>
								</div>
							)}
							<Typography type="span" size="xs" weight="bold" color={isGoalMuted ? "grey-tertiary" : "text"}>
								{Math.round(goalPercent)}%
							</Typography>
						</div>
					) : (
						<Typography type="span" size="xs" className="min-w-0 flex-1 text-center">
							Create your goal for the stream
						</Typography>
					)}

					<DropdownMenu>
						<DropdownMenuTrigger className="hover:cursor-pointer hover:opacity-60">
							<Image src={ICON_PATHS.POINTS} alt="" width={24} height={24} className="size-6 object-contain" />
						</DropdownMenuTrigger>

						<DropdownMenuContent align="end" className="min-w-24 p-0">
							{firstGoalInQueue === null ? (
								<DropdownMenuItem onClick={handleOpenGoalSettingsDialog} className="gap-2 px-2 py-2">
									<Image src={ICON_PATHS.GOAL} alt="" width={15} height={15} />
									<Typography type="span" size="sm">
										Create
									</Typography>
								</DropdownMenuItem>
							) : (
								<>
									<DropdownMenuItem
										onClick={handleStartGoal}
										disabled={isGoalActionPending || isGoalInProgress || isGoalDisabled}
										className="gap-2 px-2 py-2"
									>
										<Image src={ICON_PATHS.PLAY} alt="" width={24} height={24} />
										<Typography type="span" size="sm">
											Start
										</Typography>
									</DropdownMenuItem>
									<DropdownMenuItem onClick={handleOpenGoalSettingsDialog} className="gap-2 px-2 py-2">
										<Image src={ICON_PATHS.PENCIL} alt="" width={24} height={24} />
										<Typography type="span" size="sm">
											Edit
										</Typography>
									</DropdownMenuItem>
									<DropdownMenuItem
										onClick={handleOpenDeleteGoalDialog}
										disabled={isGoalActionPending}
										className="gap-2 px-2 py-2"
									>
										<Image src={ICON_PATHS.TRASH} alt="" width={24} height={24} />
										<Typography type="span" size="sm">
											Delete
										</Typography>
									</DropdownMenuItem>
								</>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			) : null}
		</div>
	) : (
		<div className="mx-1 mb-1.5 flex flex-col gap-1.5 rounded-2xl border border-primary bg-block p-5">
			{isPendingGoalList ? (
				<Stack spacing={12} wide className="bg-block">
					<Stack direction="row" justify="between" align="center" wide>
						<div className="flex min-w-0 items-center gap-2.5">
							<div className={cn(SKELETON_BLOCK_CLASS, "h-10 w-6 rounded-xl")} />
							<div className={cn(SKELETON_BLOCK_CLASS, "h-4.5 w-12")} />
							<div className={cn(SKELETON_BLOCK_CLASS, "h-4.5 w-30")} />
						</div>
						<div className="flex gap-1.25">
							<div className={cn(SKELETON_BLOCK_CLASS, "size-8 rounded-full")} />
							<div className={cn(SKELETON_BLOCK_CLASS, "size-8 rounded-full")} />
							<div className={cn(SKELETON_BLOCK_CLASS, "size-8 rounded-full")} />
						</div>
					</Stack>
					<div className={cn(SKELETON_BLOCK_CLASS, "h-5.25 w-full rounded-2xl")} />
				</Stack>
			) : isSuccessGoalList ? (
				<>
					<Stack wide direction="row" align="center" justify="between">
						<Stack
							align="center"
							direction="row"
							spacing={10}
							className={cn("min-w-0", firstGoalInQueue !== null && "flex-1")}
						>
							<Image
								className={cn(isGoalMuted && "opacity-50")}
								src={ICON_PATHS.GOAL}
								alt="Goal"
								width={24}
								height={24}
							/>
							<div className="flex min-w-0 items-center gap-1.5">
								<Typography
									weight="bold"
									type="p"
									size="base"
									className={cn(firstGoalInQueue !== null && "text-[#EED16B]", isGoalMuted && "text-grey-tertiary")}
								>
									{firstGoalInQueue !== null ? "Goal:" : "Goals"}
								</Typography>
								{firstGoalInQueue !== null ? (
									<div className="min-w-0 flex-1">
										<Typography
											weight="bold"
											size="base"
											className={cn(
												"block overflow-hidden text-ellipsis whitespace-nowrap",
												isGoalMuted && "text-grey-tertiary",
											)}
										>
											{firstGoalInQueue.title}
										</Typography>
									</div>
								) : null}
							</div>
						</Stack>

						{firstGoalInQueue === null ? (
							<Typography size="sm" className="max-[450px]:text-[10px]">
								Create your goal for the stream
							</Typography>
						) : null}

						{firstGoalInQueue !== null ? (
							<Stack direction="row" align="center" spacing={5}>
								<Button
									aria-label="Start goal"
									onClick={handleStartGoal}
									disable={isGoalActionPending || isGoalInProgress || isGoalDisabled}
									shape="circle"
									size="auto"
									type={isGoalPending ? "primary" : "secondary"}
									className="size-8 p-0"
									icon={<Image className="ml-0.5" src={ICON_PATHS.PLAY} alt="" />}
								/>
								<Button
									aria-label="Edit goal"
									onClick={handleOpenGoalSettingsDialog}
									shape="circle"
									size="auto"
									type="secondary"
									className="size-8 p-0"
									icon={<Image className="-translate-y-0.5" src={ICON_PATHS.PENCIL} alt="" />}
								/>
								<Button
									aria-label="Delete goal"
									onClick={handleOpenDeleteGoalDialog}
									shape="circle"
									size="auto"
									type="secondary"
									disable={isGoalActionPending}
									className="size-8 p-0"
									icon={<Image width={26} height={26} src={ICON_PATHS.TRASH} alt="" />}
								/>
							</Stack>
						) : (
							<div className="min-w-25">
								<Button aria-label="Create goal" size="small" wide onClick={handleOpenGoalSettingsDialog}>
									<Typography type="span" size="xs" weight="bold">
										Create
									</Typography>
								</Button>
							</div>
						)}
					</Stack>

					{firstGoalInQueue !== null ? (
						<div className="relative h-5.25 w-full overflow-hidden rounded-2xl bg-[#36333c]">
							<div
								className={cn(
									"ease absolute inset-0 rounded-[inherit] transition-[width] duration-200",
									isGoalDisabled ? "bg-grey-tertiary" : "bg-[linear-gradient(90deg,#2787fa_0%,#f026ab_100%)]",
								)}
								style={{ width: `${goalPercent}%` }}
							/>
							<div className="relative z-base flex h-full w-full items-center justify-between px-2.5">
								<Typography size="xs" className={cn(isGoalPending && "text-grey-tertiary")}>
									{firstGoalInQueue.current_tokens ?? 0}/{firstGoalInQueue.target_tokens} tk
								</Typography>

								<Typography size="xs" className={cn(isGoalMuted && "text-grey-tertiary")} weight="bold">
									{Math.round(goalPercent)}%
								</Typography>
							</div>
						</div>
					) : null}
				</>
			) : null}
		</div>
	);
};
